import {
  AppShell as MantineAppShell,
  Box,
  Flex,
  MantineColor,
  MantineSpacing,
} from '@mantine/core';
import { useFavicon, useHover, useMediaQuery } from '@mantine/hooks';
import { memo, useEffect, useRef } from 'react';
import { HitSlop } from 'src/common/components/HitSlop';
import { Outlet } from 'react-router';
import { UsersProvider } from 'src/admin/users/context/UsersContext';
import { ClientsProvider } from 'src/clients/context/ClientsContext';
import { CurrentClientProvider } from 'src/clients/context/CurrentClientProvider';
import { AppContainerProvider, useAppContainerContext } from './AppContainerContext';
import { AppContentPane } from './AppContentPane';
import { AppSidebar } from './AppSidebar';
import { DefaultSidebar } from './DefaultSidebar';
import { SidebarContent } from './SidebarContent';
import Header, { HEADER_HEIGHT } from './header/Header';
import { STANDARD_SIDEBAR_WIDTH, useSidebarResize } from './useDragAnimation';

type AppContainerProps = {
  skipDefaultSidebar?: boolean;
  bg?: MantineColor;
  p?: MantineSpacing;
  children?: React.ReactNode;
  initialSidebarCollapsed?: boolean;
};

function AppContainer({
  skipDefaultSidebar,
  bg,
  p,
  children,
  initialSidebarCollapsed,
}: AppContainerProps) {
  useFavicon('https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/smallLogo.svg');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <CurrentClientProvider>
      <ClientsProvider>
        <UsersProvider>
          <AppContainerProvider
            skipDefaultSidebar={skipDefaultSidebar}
            initialSidebarCollapsed={initialSidebarCollapsed}
          >
            <AppContainerLayout bg={bg} p={p}>
              {children}
            </AppContainerLayout>
          </AppContainerProvider>
        </UsersProvider>
      </ClientsProvider>
    </CurrentClientProvider>
  );
}

function AppContainerLayout({
  bg,
  p,
  children,
}: {
  bg?: MantineColor;
  p?: MantineSpacing;
  children?: React.ReactNode;
}) {
  const {
    sidebarContent,
    sidebarCollapsed,
    setSidebarCollapsed,
    contentPaneConfig,
    sidebarFullScreen,
  } = useAppContainerContext();
  const resolvedBg = contentPaneConfig.bg ?? bg;
  const resolvedP = contentPaneConfig.p ?? p;
  const containerRef = useRef<HTMLDivElement>(null);
  const { sidebarWidth, isDragging, handleMouseDown } = useSidebarResize({ containerRef });
  const { hovered, ref: hoverRef } = useHover();

  const isNarrowViewport = useMediaQuery(`(max-width: ${STANDARD_SIDEBAR_WIDTH * 2}px)`);

  useEffect(() => {
    if (isNarrowViewport && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  }, [isNarrowViewport, sidebarCollapsed, setSidebarCollapsed]);

  const sidebar = sidebarContent ?? <DefaultSidebar />;

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <MantineAppShell
      header={{
        height: HEADER_HEIGHT,
      }}
    >
      <Header showChat={handleToggleSidebar} isChatOpened={!sidebarCollapsed} bg="gray.10" />
      <MantineAppShell.Main bg="gray.10">
        <Flex
          gap={sidebarFullScreen ? 0 : 'xxxs'}
          ref={containerRef}
          bg="gray.10"
          p="xxs"
          style={{
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflow: 'hidden',
          }}
        >
          <AppContentPane bg={resolvedBg} p={resolvedP} collapsed={sidebarFullScreen}>
            {children ?? <Outlet />}
          </AppContentPane>

          <HitSlop
            ref={hoverRef}
            amount={sidebarFullScreen ? 0 : 12}
            cursor="col-resize"
            zIndex={10}
            onMouseDown={handleMouseDown}
            style={{
              alignSelf: 'center',
              flexShrink: 0,
              width: sidebarFullScreen ? 0 : undefined,
              overflow: 'hidden',
              opacity: sidebarCollapsed || sidebarFullScreen ? 0 : 1,
              pointerEvents: sidebarCollapsed || sidebarFullScreen ? 'none' : 'auto',
              transition: 'opacity 0.25s ease-out, width 0.25s ease-out',
            }}
          >
            <Box
              bg={hovered ? 'brand.5' : 'brand.6'}
              style={{
                width: 'var(--mantine-spacing-xxxs)',
                height: 'var(--mantine-spacing-xxl)',
                borderRadius: 12,
                transition: 'background-color 0.2s ease',
                pointerEvents: 'none',
              }}
            />
          </HitSlop>

          <AppSidebar
            width={sidebarWidth}
            isDragging={isDragging}
            collapsed={sidebarCollapsed}
            fullScreen={sidebarFullScreen}
          >
            {sidebar}
          </AppSidebar>
        </Flex>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}

type AppContainerComponent = React.MemoExoticComponent<typeof AppContainer> & {
  SidebarContent: typeof SidebarContent;
};

const MemoizedAppContainer = memo(AppContainer) as AppContainerComponent;
MemoizedAppContainer.SidebarContent = SidebarContent;

export default MemoizedAppContainer;
